import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, AlertCircle, Download, ShieldCheck, X as XIcon } from 'lucide-react';
import api from '../../lib/api';

/**
 * AVV / DPA digital signing card. Extracted from SettingsPage.jsx.
 * Self-contained: fetches its own status, renders modal, dispatches
 * `studioops:avv-updated` so the DashboardLayout banner clears.
 */
export default function AvvCard({ className = '', t }) {
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState('de');

  const load = React.useCallback(() => {
    api.get('/api/avv/status').then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  async function openSignModal() {
    setBusy(true);
    try {
      const r = await api.get(`/api/avv/preview?lang=${lang}`);
      setPreviewText(r.data.text || '');
      setAccepted(false);
      setShowModal(true);
    } finally { setBusy(false); }
  }

  async function signNow() {
    if (!accepted) return;
    const pid = localStorage.getItem('studioops_active_practice');
    setBusy(true);
    try {
      const headers = {};
      if (pid) headers['X-Practice-Id'] = pid;
      await api.post('/api/avv/sign', { accepted: true }, { headers });
      toast.success(t('settings.avv.signed'));
      setShowModal(false);
      await load();
      window.dispatchEvent(new CustomEvent('studioops:avv-updated'));
    } catch (e) {
      const code = e.response?.data?.error;
      if (code === 'practice_required') toast.error(t('settings.avv.pickPracticeFirst'));
      else toast.error(e.response?.data?.message || t('settings.avv.signFailed'));
    } finally { setBusy(false); }
  }

  function downloadPdf() {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/avv/download?lang=${lang}`;
    const headers = { Authorization: `Bearer ${localStorage.getItem('studioops_access_token')}` };
    const pid = localStorage.getItem('studioops_active_practice');
    if (pid) headers['X-Practice-Id'] = pid;
    fetch(url, { headers })
      .then((r) => { if (!r.ok) throw new Error('http_' + r.status); return r.blob(); })
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `${lang === 'en' ? 'DPA' : 'AVV'}_StudioOps.pdf`;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(objUrl); a.remove(); }, 500);
      })
      .catch(() => toast.error(t('settings.avv.downloadFailed')));
  }

  const signed = status?.signed;
  const needsSigning = !!status?.needs_signing;
  const outdated = !!status?.outdated;
  const fmtDate = (d) => d ? new Date(d).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className={`rounded-md border border-border bg-white p-4 ${className}`} data-testid="avv-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <ShieldCheck size={14} className="text-accent"/>
          {t('settings.avv.title')} {lang === 'en' ? '(DPA · English)' : '(AVV)'}
        </h3>
        <div className="inline-flex rounded-full bg-border/40 p-0.5 text-[11px]">
          <button onClick={() => setLang('de')} className={`px-2.5 py-1 rounded-full transition-colors ${lang === 'de' ? 'bg-white text-navy font-medium shadow-sm' : 'text-text-secondary'}`} data-testid="avv-lang-de">Deutsch</button>
          <button onClick={() => setLang('en')} className={`px-2.5 py-1 rounded-full transition-colors ${lang === 'en' ? 'bg-white text-navy font-medium shadow-sm' : 'text-text-secondary'}`} data-testid="avv-lang-en">English</button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="avv-status">
        {signed && !outdated ? (
          <>
            <span className="so-pill so-pill-success flex items-center gap-1" data-testid="avv-status-signed">
              <Check size={11}/>{t('settings.avv.signedOn', { date: fmtDate(status.signed_at) })}
            </span>
            {status?.signed_by_email && (
              <span className="text-xs text-text-secondary">{t('settings.avv.signedBy')} <strong>{status.signed_by_email}</strong> · Version {status.avv_version}</span>
            )}
          </>
        ) : signed && outdated ? (
          <>
            <span className="so-pill so-pill-warning flex items-center gap-1" data-testid="avv-status-outdated">
              <AlertCircle size={11}/>{t('settings.avv.outdated')}
            </span>
            <span className="text-xs text-text-secondary">{t('settings.avv.yourVersion')}: <strong>{status.avv_version}</strong> · {t('settings.avv.currentVersion')}: <strong>{status.version}</strong></span>
          </>
        ) : (
          <span className="so-pill so-pill-danger flex items-center gap-1" data-testid="avv-status-pending">
            <AlertCircle size={11}/>{t('settings.avv.pending')}
          </span>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {needsSigning && (
          <button onClick={openSignModal} disabled={busy} className="so-btn so-btn-primary text-xs" data-testid="avv-sign-btn">
            {outdated ? t('settings.avv.signUpdatedBtn') : t('settings.avv.signBtn')}
          </button>
        )}
        {signed && (
          <button onClick={downloadPdf} disabled={busy} className="so-btn so-btn-secondary text-xs" data-testid="avv-download-btn">
            <Download size={12}/>{t('settings.avv.downloadBtn', { kind: lang === 'en' ? 'DPA' : 'AVV' })}
          </button>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="avv-modal" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-md shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold">{lang === 'en' ? 'Data Processing Agreement (DPA)' : 'Auftragsverarbeitungsvertrag (AVV)'}</h3>
              <button onClick={() => setShowModal(false)} className="text-text-secondary hover:text-text" aria-label={t('common.close')}><XIcon size={18}/></button>
            </div>
            <div className="px-5 py-3 overflow-y-auto flex-1">
              <pre className="font-sans text-[12px] leading-relaxed whitespace-pre-wrap text-text" data-testid="avv-modal-text">{previewText}</pre>
            </div>
            <div className="px-5 py-3 border-t border-border bg-background/50">
              <label className="flex items-start gap-2 text-sm mb-3 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="avv-accept-checkbox"/>
                <span>{t('settings.avv.modalAccept', { kind: lang === 'en' ? 'DPA' : 'AVV' })}</span>
              </label>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowModal(false)} className="so-btn so-btn-secondary text-xs" data-testid="avv-modal-cancel">{t('common.cancel')}</button>
                <button onClick={signNow} disabled={!accepted || busy} className="so-btn so-btn-primary text-xs" data-testid="avv-modal-confirm">{t('settings.avv.signNow')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
